import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ArrowUpRight,
  Car,
  Download,
  MapPin,
  RefreshCw,
  Star,
  TrendingUp,
  Users,
  Wallet,
  Wifi,
  WifiOff,
  XCircle,
} from 'lucide-react'
import {
  mapAdminRide,
  mapFinanceOverview,
  mapOverviewReport,
  type AdminRide,
  type FinanceOverviewData,
  type OverviewReport,
} from '../../lib/adminMappers'
import { cn, formatCFA, formatDate } from '../../lib/utils'
import { dashboardApi, financeApi, ridesApi, usersApi } from '../../services/api'
import {
  useDebouncedCallback,
  useRealtimeAdmin,
  useRealtimeConnection,
  type RideCreatedEvent,
  type StatsUpdatedEvent,
} from '../../hooks'
import { useAuthStore } from '../../stores/authStore'

const VEHICLE_COLORS = ['#F5A623', '#3B82F6', '#22C55E', '#8B5CF6']

type UserStats = {
  total_users: number
  total_clients: number
  total_drivers: number
  active_drivers: number
  new_users_today: number
  new_users_week: number
}

type RideStats = {
  total_rides: number
  completed_rides: number
  cancelled_rides: number
  pending_rides: number
  in_progress_rides: number
}

const MOCK_OVERVIEW: OverviewReport = {
  stats: {
    totalRides: 156,
    totalRevenue: 972500,
    totalDrivers: 42,
    totalUsers: 214,
    avgRating: 4.7,
    cancelRate: 6,
  },
  daily: [
    { date: '2026-03-18', name: 'Mar', rides: 18, revenue: 111000 },
    { date: '2026-03-19', name: 'Mer', rides: 21, revenue: 129500 },
    { date: '2026-03-20', name: 'Jeu', rides: 19, revenue: 118000 },
    { date: '2026-03-21', name: 'Ven', rides: 25, revenue: 160000 },
    { date: '2026-03-22', name: 'Sam', rides: 31, revenue: 201000 },
    { date: '2026-03-23', name: 'Dim', rides: 23, revenue: 143500 },
    { date: '2026-03-24', name: 'Lun', rides: 19, revenue: 109500 },
  ],
  ridesByType: [
    { name: 'standard', value: 78 },
    { name: 'comfort', value: 39 },
    { name: 'xl', value: 24 },
    { name: 'moto', value: 15 },
  ],
  topZones: [
    { name: 'Ouaga 2000', value: 34, revenue: 238000, avgDistanceKm: 6.8, avgDurationMin: 19, cancelRate: 4 },
    { name: 'Koulouba', value: 28, revenue: 194500, avgDistanceKm: 5.4, avgDurationMin: 16, cancelRate: 5 },
    { name: 'Dassasgho', value: 22, revenue: 153000, avgDistanceKm: 4.9, avgDurationMin: 14, cancelRate: 7 },
  ],
  hourly: [],
}

const MOCK_FINANCE: FinanceOverviewData = {
  totalRevenue: 972500,
  revenueGrowth: 8.4,
  totalCommissions: 145875,
  commissionsGrowth: 6.9,
  pendingPayouts: 51200,
  walletBalance: 76400,
  byMethod: [
    { name: 'Wallet', value: 284000 },
    { name: 'Espèces', value: 361000 },
    { name: 'Mobile Money', value: 327500 },
  ],
}

const MOCK_USER_STATS: Partial<UserStats> = {
  total_users: 214,
  total_clients: 172,
  total_drivers: 42,
  active_drivers: 27,
  new_users_today: 8,
  new_users_week: 29,
}

const MOCK_RIDE_STATS: Partial<RideStats> = {
  total_rides: 156,
  completed_rides: 132,
  cancelled_rides: 9,
  pending_rides: 11,
  in_progress_rides: 4,
}

const MOCK_RECENT_RIDES: AdminRide[] = [
  {
    id: '10241',
    numericId: 10241,
    status: 'completed',
    client: 'Aminata Sawadogo',
    clientPhone: '+22670123456',
    driver: 'Seydou Ouédraogo',
    driverPhone: '+22675987654',
    from: 'Ouaga 2000',
    to: 'Koulouba',
    amount: 4200,
    vehicle: 'standard',
    payment: 'mobile_money',
    distanceKm: 6.3,
    durationMin: 18,
    createdAt: new Date().toISOString(),
    rating: 5,
  },
  {
    id: '10240',
    numericId: 10240,
    status: 'in_progress',
    client: 'Ibrahim Koné',
    clientPhone: '+22670234567',
    driver: 'Moussa Traoré',
    driverPhone: '+22675876543',
    from: 'Patte d\'Oie',
    to: 'Dassasgho',
    amount: 3600,
    vehicle: 'comfort',
    payment: 'wallet',
    distanceKm: 5.1,
    durationMin: 15,
    createdAt: new Date().toISOString(),
    rating: null,
  },
]

export default function DashboardPage() {
  const [period, setPeriod] = useState<'today' | 'week' | 'month'>('week')
  const [lastUpdate, setLastUpdate] = useState<string | null>(null)
  const [newEventsCount, setNewEventsCount] = useState(0)
  // État réel de la connexion Pusher (et non plus la simple présence d'une clé).
  const { isConnected: isRealtimeConnected } = useRealtimeConnection()
  const periodParams = getPeriodParams(period)
  const queryClient = useQueryClient()
  // Permissions réellement accordées : filtrent les requêtes du dashboard
  // pour ne jamais appeler un endpoint refusé par le backend (403 inutile).
  const { hasPermission } = useAuthStore()

  // Indicateur d'activité « temps réel » : chaque événement du domaine
  // incrémente le compteur et rafraîchit l'horodatage. Les données, elles,
  // suivent les signaux d'invalidation envoyés par le backend.
  const handleActivity = useCallback(() => {
    setLastUpdate(new Date().toLocaleTimeString())
    setNewEventsCount(prev => prev + 1)
  }, [])

  // Nouvelle course : la liste des courses récentes est rafraîchie sans attendre
  // le signal d'agrégats, puisqu'elle est visible immédiatement.
  const handleRideCreated = useCallback((event: RideCreatedEvent) => {
    handleActivity()
    queryClient.invalidateQueries({ queryKey: ['dashboard-recent-rides'] })
  }, [handleActivity, queryClient])

  // Agrégats : le backend signale `stats.updated` sur `admin.dashboard`
  // (`DashboardStatsUpdated`) à chaque changement de course ou de chauffeur ; le
  // motif reçu cible les requêtes à relancer. Le débounce amortit les rafales
  // (une course émet plusieurs événements) et évite d'empiler les appels HTTP.
  const handleStatsUpdated = useDebouncedCallback((event: StatsUpdatedEvent) => {
    setLastUpdate(new Date().toLocaleTimeString())

    queryClient.invalidateQueries({ queryKey: ['dashboard-overview'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard-finance-overview'] })

    if (event.reason.startsWith('ride')) {
      queryClient.invalidateQueries({ queryKey: ['dashboard-ride-stats'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-recent-rides'] })
    }

    if (event.reason.startsWith('driver')) {
      queryClient.invalidateQueries({ queryKey: ['dashboard-user-stats'] })
    }
  }, 800)

  // Écouter les mises à jour en temps réel (canaux staff : admin.rides,
  // admin.drivers, admin.dashboard — les notifications personnelles sont
  // écoutées séparément par le layout et la page Notifications).
  useRealtimeAdmin({
    onRideCreated: handleRideCreated,
    onRideUpdated: handleActivity,
    onDriverStatusChanged: handleActivity,
    onStatsUpdated: handleStatsUpdated,
  })

  const handleRefresh = useCallback(() => {
    setNewEventsCount(0)
    queryClient.invalidateQueries()
  }, [queryClient])

  const overviewQuery = useQuery({
    queryKey: ['dashboard-overview', period, periodParams.start_date, periodParams.end_date],
    queryFn: async () => {
      const response = await dashboardApi.getOverview(periodParams)
      return mapOverviewReport(response.data?.data)
    },
    staleTime: 30_000,
  })

  const financeQuery = useQuery({
    queryKey: ['dashboard-finance-overview'],
    queryFn: async () => {
      const response = await financeApi.overview()
      return mapFinanceOverview(response.data?.data)
    },
    enabled: hasPermission('finance.view'),
    staleTime: 30_000,
  })

  const userStatsQuery = useQuery({
    queryKey: ['dashboard-user-stats'],
    queryFn: async () => {
      const response = await usersApi.stats()
      return (response.data?.data ?? {}) as Partial<UserStats>
    },
    enabled: hasPermission('users.view'),
    staleTime: 30_000,
  })

  const rideStatsQuery = useQuery({
    queryKey: ['dashboard-ride-stats'],
    queryFn: async () => {
      const response = await ridesApi.stats()
      return (response.data?.data ?? {}) as Partial<RideStats>
    },
    enabled: hasPermission('rides.view'),
    staleTime: 30_000,
  })

  const recentRidesQuery = useQuery({
    queryKey: ['dashboard-recent-rides'],
    queryFn: async () => {
      const response = await ridesApi.list({ page: 1 })
      return Array.isArray(response.data?.data)
        ? response.data.data.slice(0, 5).map((ride: unknown) => mapAdminRide(ride))
        : []
    },
    enabled: hasPermission('rides.view'),
    staleTime: 15_000,
  })

  const overview = overviewQuery.data
  const finance = financeQuery.data
  const userStats = userStatsQuery.data
  const rideStats = rideStatsQuery.data
  const recentRides: AdminRide[] = recentRidesQuery.data ?? []

  const shouldUseOverviewMock =
    overviewQuery.isError ||
    !overview ||
    (overview.daily.length === 0 && overview.ridesByType.length === 0 && overview.topZones.length === 0)
  const shouldUseFinanceMock = financeQuery.isError || !finance
  const shouldUseUserStatsMock = userStatsQuery.isError || !userStats
  const shouldUseRideStatsMock = rideStatsQuery.isError || !rideStats
  const shouldUseRecentRidesMock = recentRidesQuery.isError || recentRides.length === 0

  const resolvedOverview = shouldUseOverviewMock ? MOCK_OVERVIEW : overview
  const resolvedFinance = shouldUseFinanceMock ? MOCK_FINANCE : finance
  const resolvedUserStats = shouldUseUserStatsMock ? MOCK_USER_STATS : userStats
  const resolvedRideStats = shouldUseRideStatsMock ? MOCK_RIDE_STATS : rideStats
  const resolvedRecentRides = shouldUseRecentRidesMock ? MOCK_RECENT_RIDES : recentRides

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
          <p className="text-sm text-gray-500 mt-1">Vue de synthèse alimentée par les endpoints admin réels</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Indicateur temps réel */}
          <div className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium',
            isRealtimeConnected
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-500'
          )}>
            {isRealtimeConnected ? (
              <>
                <Wifi className="w-3.5 h-3.5" />
                <span>Temps réel</span>
                {lastUpdate && <span className="text-green-500 ml-1">• {lastUpdate}</span>}
              </>
            ) : (
              <>
                <WifiOff className="w-3.5 h-3.5" />
                <span>Hors ligne</span>
              </>
            )}
          </div>

          {/* Badge nouvelles courses */}
          {newEventsCount > 0 && (
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium hover:bg-amber-200 transition-colors animate-pulse"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {newEventsCount} mise{newEventsCount > 1 ? 's' : ''} à jour
            </button>
          )}

          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl p-1">
            {(['today', 'week', 'month'] as const).map((value) => (
              <button
                key={value}
                onClick={() => setPeriod(value)}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                  period === value
                    ? 'bg-amber-400 text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                )}
              >
                {{ today: "Aujourd'hui", week: 'Semaine', month: 'Mois' }[value]}
              </button>
            ))}
          </div>
          <button className="p-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors" title="Télécharger le rapport">
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {(overviewQuery.isError || financeQuery.isError || userStatsQuery.isError || rideStatsQuery.isError || recentRidesQuery.isError) && (
        <div className="px-4 py-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl">
          Une partie du dashboard n’a pas pu être chargée. Des données fictives de secours sont affichées sur les blocs concernés.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Courses"
          value={(resolvedOverview.stats.totalRides ?? resolvedRideStats.total_rides ?? 0).toLocaleString()}
          icon={<Car className="w-5 h-5" />}
          color="amber"
        />
        <StatCard
          label="Revenus"
          value={formatCompactCurrency(resolvedFinance.totalRevenue ?? resolvedOverview.stats.totalRevenue ?? 0)}
          subtitle="FCFA"
          icon={<Wallet className="w-5 h-5" />}
          color="green"
        />
        <StatCard
          label="Chauffeurs"
          value={`${resolvedUserStats.active_drivers ?? 0}/${resolvedUserStats.total_drivers ?? 0}`}
          subtitle="actifs / total"
          icon={<Users className="w-5 h-5" />}
          color="blue"
        />
        <StatCard
          label="Clients"
          value={(resolvedUserStats.total_clients ?? resolvedOverview.stats.totalUsers ?? 0).toLocaleString()}
          subtitle={period === 'today' ? `${resolvedUserStats.new_users_today ?? 0} nouveaux comptes` : `${resolvedUserStats.new_users_week ?? 0} nouveaux comptes`}
          icon={<TrendingUp className="w-5 h-5" />}
          color="purple"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Complétées" value={String(resolvedRideStats.completed_rides ?? 0)} icon={<Car className="w-4 h-4 text-green-500" />} color="green" />
        <MetricCard label="En attente" value={String(resolvedRideStats.pending_rides ?? 0)} icon={<RefreshCw className="w-4 h-4 text-amber-500" />} color="amber" />
        <MetricCard label="Annulations" value={`${resolvedOverview.stats.cancelRate ?? 0}%`} icon={<XCircle className="w-4 h-4 text-red-500" />} color="red" />
        <MetricCard label="Note moyenne" value={`${resolvedOverview.stats.avgRating ?? 0}/5`} icon={<Star className="w-4 h-4 text-amber-500 fill-amber-500" />} color="purple" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white border border-gray-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-semibold text-gray-900">Courses et revenus</h2>
            <Link to="/reports" className="text-sm text-amber-500 hover:text-amber-600">Voir les rapports</Link>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={resolvedOverview.daily ?? []} barGap={8}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9CA3AF' }} />
              <YAxis yAxisId="rides" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9CA3AF' }} />
              <YAxis yAxisId="revenue" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9CA3AF' }} />
              <Tooltip
                formatter={(value: unknown, name: unknown) => [
                  name === 'revenue' ? formatCFA(Number(value)) : value as string | number,
                  name === 'revenue' ? 'Revenus' : 'Courses',
                ]}
              />
              <Bar yAxisId="rides" dataKey="rides" fill="#DBEAFE" radius={[6, 6, 0, 0]} maxBarSize={28} />
              <Bar yAxisId="revenue" dataKey="revenue" fill="#F5A623" radius={[6, 6, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Répartition véhicules</h2>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={resolvedOverview.ridesByType ?? []} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                {(resolvedOverview.ridesByType ?? []).map((entry, index) => (
                  <Cell key={entry.name} fill={VEHICLE_COLORS[index % VEHICLE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: unknown) => [Number(value), 'Courses']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-2">
            {(resolvedOverview.ridesByType ?? []).map((item, index) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: VEHICLE_COLORS[index % VEHICLE_COLORS.length] }} />
                  <span className="text-sm text-gray-600 capitalize">{item.name}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-500" />
              Zones actives
            </h2>
            <Link to="/reports" className="text-sm text-amber-500 hover:text-amber-600">Détail</Link>
          </div>
          <div className="space-y-3">
            {(resolvedOverview.topZones ?? []).map((zone) => (
              <div key={zone.name} className="border border-gray-100 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{zone.name}</span>
                  <span className="text-sm text-gray-500">{zone.value} courses</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                  <span>Revenus: {formatCFA(zone.revenue)}</span>
                  <span>Dist.: {zone.avgDistanceKm.toFixed(1)} km</span>
                  <span>Durée: {zone.avgDurationMin.toFixed(0)} min</span>
                  <span>Annulation: {zone.cancelRate}%</span>
                </div>
              </div>
            ))}

            {(resolvedOverview.topZones ?? []).length === 0 && (
              <div className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-xl p-6 text-center">
                Aucune activité de zone disponible sur cette période.
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Courses récentes</h2>
            <Link to="/rides" className="flex items-center gap-1 text-sm text-amber-500 font-medium hover:text-amber-600">
              Voir tout
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {['ID', 'Client', 'Chauffeur', 'Trajet', 'Montant', 'Statut', 'Date'].map((heading) => (
                    <th key={heading} className="text-left text-xs text-gray-400 font-medium px-6 py-3">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resolvedRecentRides.map((ride) => (
                  <tr key={ride.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 font-mono text-gray-500">#{ride.id}</td>
                    <td className="px-6 py-3 font-medium text-gray-900">{ride.client}</td>
                    <td className="px-6 py-3 text-gray-600">{ride.driver ?? 'En attente'}</td>
                    <td className="px-6 py-3 text-gray-600 max-w-[220px] truncate">{ride.from} → {ride.to}</td>
                    <td className="px-6 py-3 font-semibold text-gray-900">{formatCFA(ride.amount)}</td>
                    <td className="px-6 py-3">
                      <StatusBadge status={ride.status} />
                    </td>
                    <td className="px-6 py-3 text-gray-400">{ride.createdAt ? formatDate(ride.createdAt) : 'N/A'}</td>
                  </tr>
                ))}

                {!recentRidesQuery.isLoading && resolvedRecentRides.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                      Aucune course récente disponible.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  subtitle,
  icon,
  color,
}: {
  label: string
  value: string
  subtitle?: string
  icon: React.ReactNode
  color: 'amber' | 'green' | 'blue' | 'purple'
}) {
  const colors = {
    amber: { icon: 'bg-amber-400' },
    green: { icon: 'bg-green-500' },
    blue: { icon: 'bg-blue-500' },
    purple: { icon: 'bg-purple-500' },
  }[color]

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-start justify-between">
        <div className={cn('p-2.5 rounded-xl text-white', colors.icon)}>{icon}</div>
      </div>
      <div className="mt-4">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500 mt-1">{subtitle ?? label}</p>
        {subtitle && <p className="text-xs text-gray-400">{label}</p>}
      </div>
    </div>
  )
}

function MetricCard({
  label,
  value,
  icon,
  color,
}: {
  label: string
  value: string
  icon: React.ReactNode
  color: 'green' | 'amber' | 'red' | 'purple'
}) {
  const borderColors = {
    green: 'border-l-green-500',
    amber: 'border-l-amber-500',
    red: 'border-l-red-500',
    purple: 'border-l-purple-500',
  }

  return (
    <div className={cn('bg-white border border-gray-200 border-l-4 rounded-xl p-4', borderColors[color])}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-gray-500 font-medium">{label}</span>
      </div>
      <span className="text-xl font-bold text-gray-900">{value}</span>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const statusMap = {
    completed: { label: 'Terminée', className: 'bg-green-100 text-green-700' },
    in_progress: { label: 'En cours', className: 'bg-blue-100 text-blue-700' },
    pending: { label: 'En attente', className: 'bg-amber-100 text-amber-700' },
    cancelled: { label: 'Annulée', className: 'bg-red-100 text-red-600' },
    accepted: { label: 'Acceptée', className: 'bg-purple-100 text-purple-700' },
  } as const

  const config = statusMap[status as keyof typeof statusMap] ?? { label: status, className: 'bg-gray-100 text-gray-600' }

  return (
    <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium', config.className)}>
      {config.label}
    </span>
  )
}

function getPeriodParams(period: 'today' | 'week' | 'month'): { start_date: string; end_date: string } {
  const end = new Date()
  const start = new Date()

  if (period === 'today') {
    // same day
  } else if (period === 'week') {
    start.setDate(start.getDate() - 7)
  } else {
    start.setMonth(start.getMonth() - 1)
  }

  return {
    start_date: start.toISOString().split('T')[0] ?? '',
    end_date: end.toISOString().split('T')[0] ?? '',
  }
}

function formatCompactCurrency(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(value)
}
